import { AHclimOpModeEnum, AHglobStatusEnum, AHheatPumpType, productProperty } from 'ngx-core-services';

export const getLabels = () => ({
  // Generic
  localeCode: 'en-EN',
  title: 'Heat pump',
  status: 'Status',

  // Image card
  deviceType: 'Device type',
  deviceTypeValue: 'Heat pump',

  // DHW
  dhwTitle: 'Domestic hot water',
  dhwEnabled: 'Enabled',
  dhwDisabled: 'Disabled',
  dhwTempTitle: 'DHW temperature',

  // Climate
  climateTitle: 'Climate control',
  climateEnabled: 'Enabled',
  climateDisabled: 'Disabled',
  climateModeTitle: 'Climate mode',
  climateModeCooling: 'Cooling',
  climateModeHeating: 'Heating',
  climateCoolingTempTitle: 'Cooling temperature',
  climateHeatingTempTitle: 'Heating temperature',

  // Misc
  model: 'Model',
  running: 'Running',
  alarm: 'Alarm',
  standby: 'Standby',
  shutdown: 'Shutdown',
  firmwareVer: 'Firmware Ver.',
  maxPower: 'Max Power',
  unknown: 'Unknown',
});

export const getStatusDetails = (
  globStatus: AHglobStatusEnum| undefined,
  labels: ReturnType<typeof getLabels>
): { label: string; cssClasses: string } => {
  switch (globStatus) {
    case AHglobStatusEnum.Running:
    case AHglobStatusEnum.Starting:
      return { label: labels.running, cssClasses: 'bg-green-100 border-green-500 text-green-500' };
    case AHglobStatusEnum.Alarm:
      return { label: labels.alarm, cssClasses: 'bg-red-100 border-red-400 text-red-400' };
    case AHglobStatusEnum.Standby:
    case AHglobStatusEnum.Shutdown:
      return { label: labels.standby, cssClasses: 'bg-accent-100 border-accent-500 text-accent-500' };
    default:
      return { label: labels.unknown, cssClasses: 'bg-grey-100 border-grey-300 text-grey-300' };
  }
};

export const buildHeatPumpProperties = (
  device: AHheatPumpType | undefined,
  labels: ReturnType<typeof getLabels>
): productProperty[] => {
  const props: productProperty[] = [];

  // Device type
  props.push({
    name: labels.deviceType,
    value: labels.deviceTypeValue,
    icon: 'hvac',
    iconCssClasses: 'text-grey-400',
  });

  // DHW section
  const dhwEnabled = !!device?.DHWOn;
  props.push({
    name: labels.dhwTitle,
    value: dhwEnabled ? labels.dhwEnabled : labels.dhwDisabled,
    icon: 'water_heater',
    iconCssClasses: 'text-grey-400',
  });
  if ( dhwEnabled ) {
    props.push({
      name: labels.dhwTempTitle,
      value: `${device.DHWSetTemp}°`,
      icon: 'thermostat',
      iconCssClasses: 'text-grey-400',
    });
  }

  // Climate section
  const climateEnabled = !!device?.climOnA;
  props.push({
    name: labels.climateTitle,
    value: climateEnabled ? labels.climateEnabled : labels.climateDisabled,
    icon: 'ac_unit',
    iconCssClasses: 'text-grey-400',
  });
  if (climateEnabled ) {
    const modeCooling = device?.climOpMode === AHclimOpModeEnum.Cooling;
    const modeValue = modeCooling ? labels.climateModeCooling : labels.climateModeHeating;
    props.push({
      name: labels.climateModeTitle,
      value: modeValue,
      icon: 'compare_arrows',
      iconCssClasses: 'text-grey-400',
    });

    props.push({
      name: labels.climateCoolingTempTitle,
      value: `${device?.climSetTempCoolingA ?? '-'}°`,
      icon: 'thermostat',
      iconCssClasses: 'text-grey-400',
    });

    props.push({
      name: labels.climateHeatingTempTitle,
      value: `${device?.climSetTempHeatingA ?? '-'}°`,
      icon: 'thermostat',
      iconCssClasses: 'text-grey-400',
    });
  }

  return props;
};
